import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog'
import { getErrorMessage } from '@/lib/error'
import { Button } from '@/components/ui/button'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import { resetStore } from '@/services/store.service'
import { HugeiconsIcon } from '@hugeicons/react'
import { DatabaseBackupIcon, TextClearIcon } from '@hugeicons/core-free-icons'
import { forwardRef } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

const DangerZonePage = forwardRef(({ index }: { index?: number }, ref) => {
  const { t } = useTranslation()

  async function clearDatabase() {
    try {
      await resetStore()
      toast.success(t('Local database cleared'))
    } catch (e) {
      toast.error(getErrorMessage(e))
    }
  }

  function clearLocalStorage() {
    try {
      localStorage.clear()
      toast.success(t('localStorage cleared'))
      setTimeout(() => window.location.reload(), 500)
    } catch (e) {
      toast.error(getErrorMessage(e))
    }
  }

  return (
    <SecondaryPageLayout ref={ref} index={index} title={t('Danger Zone')}>
      <div className="space-y-4 my-3 px-4">
        <p className="text-sm text-muted-foreground">
          {t(
            'These actions wipe local data and cannot be undone. Use only if you are stuck with corrupted state.'
          )}
        </p>
        <div className="space-y-3">
          <ConfirmButton
            label={t('Clear local database')}
            description={t('Delete all events stored in the local database and reinitialize it.')}
            onConfirm={clearDatabase}
          >
            <HugeiconsIcon icon={DatabaseBackupIcon} className="size-4" />
            {t('Clear local database')}
          </ConfirmButton>
          <ConfirmButton
            label={t('Clear localStorage')}
            description={t('Delete all settings, sessions and cached data stored in localStorage.')}
            onConfirm={clearLocalStorage}
          >
            <HugeiconsIcon icon={TextClearIcon} className="size-4" />
            {t('Clear localStorage')}
          </ConfirmButton>
        </div>
      </div>
    </SecondaryPageLayout>
  )
})
DangerZonePage.displayName = 'DangerZonePage'
export default DangerZonePage

function ConfirmButton({
  label,
  description,
  onConfirm,
  children
}: {
  label: string
  description: string
  onConfirm: () => void
  children: React.ReactNode
}) {
  const { t } = useTranslation()

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" className="w-full">
          {children}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{label}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('Cancel')}</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={onConfirm}>
            {label}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
